/* eslint-disable no-unused-vars, import/no-unresolved, react/prop-types */
import React, { cloneElement, useEffect, useRef, useState } from 'react'
import { register } from 'swiper/element/bundle'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { CardCourse } from '../../../../components'
import { useCourseList, useHistories, useProfile } from '../../../../hooks'
import { dateFormat } from '../../../../utils'
import { FULL_DATE_TIME } from '../../../../utils/date'
import { TabsName } from '../../../../constants'
import { TYPE, PARAMS } from '../../../../constants/courseList'
import Wrapper from './styled'

register()

const getKeyByType = (type, categoryId) => {
  switch (type) {
    case TYPE.NEW:
      return PARAMS.NEW
    case TYPE.RECOMMENDED:
      return PARAMS.RECOMMENDED
    case TYPE.RECENT_VIEWS:
      return PARAMS.RECENT_VIEWS
    case TYPE.CATEGORY:
      return `${PARAMS.CATEGORY}_${categoryId}`
    default:
      return ''
  }
}

const getQuery = () => new URLSearchParams(window.location.search)

const changeParams = ({ type, swiperRef, slideParams, setSlideParams, categoryId = null }) => {
  const { activeIndex } = swiperRef.current
  const key = getKeyByType(type, categoryId)

  if (key) {
    const query = getQuery()
    query.set(key, activeIndex)
    window.history.pushState({}, '', `${window.location.pathname}?${query.toString()}`)
    setSlideParams({ ...slideParams, [key]: activeIndex })
  }
}

const getInitialSlide = ({ type, categoryId = null }) => {
  const query = getQuery()
  const key = getKeyByType(type, categoryId)
  return query.get(key) ? Number(query.get(key)) : 0
}

const Swiper = (props) => {
  const { dataSource, slideParams, setSlideParams, type, isUserCompany, isNissoken, categories, categoryId } = props
  const [visible, setVisible] = useState(false)
  const swiperRef = useRef(null)
  const prevRef = useRef(null)
  const nextRef = useRef(null)
  const currentPageRef = useRef(dataSource.page)
  const isInitialLoadRef = useRef(true)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const [virtualData, setVirtualData] = useState({ from: 0, to: 0, offset: 0, slides: [] })
  // const initialSlide = getInitialSlide({ type, categoryId })

  const { profile } = useProfile()
  const {
    getCourseNissokenNew,
    getCourseNissokenRecommended,
    getCourseNissokenRecentViews,
    getCourseNissokenByCategories,
    getCourseCompanyNew,
    getCourseCompanyRecommended,
    getCourseCompanyRecentViews,
    getCourseCompanyByCategories,
    getUncategorizedCourseNissoken,
    getUncategorizedCourseCompany,
    savedFilter,
    courseListTab: currentTab
  } = useCourseList({ userId: profile.userId })

  // 追加のスライドを取得する
  const handleFetchMoreData = () => {
    const { pages } = dataSource
    const nextPage = currentPageRef.current + 1

    if (nextPage > pages) return

    const payload = { ...savedFilter, page: nextPage, initial: false }
    const LIMIT = 20

    if (TabsName.NISSOKEN_COURSE === currentTab) {
      if (type === TYPE.NEW) getCourseNissokenNew({ ...payload, type: TYPE.NEW })
      if (type === TYPE.RECOMMENDED) getCourseNissokenRecommended({ ...payload, type: TYPE.RECOMMENDED })
      if (type === TYPE.RECENT_VIEWS) getCourseNissokenRecentViews({ ...payload, type: TYPE.RECENT_VIEWS })
      if (type === TYPE.CATEGORY) getCourseNissokenByCategories({ ...payload, limit: LIMIT, categories })
      if (type === TYPE.UNCATEGORIZED) getUncategorizedCourseNissoken({ page: nextPage, limit: LIMIT })
    }

    if (TabsName.COMPANY_COURSE === currentTab) {
      if (type === TYPE.NEW) getCourseCompanyNew(payload)
      if (type === TYPE.RECOMMENDED) getCourseCompanyRecommended(payload)
      if (type === TYPE.RECENT_VIEWS) getCourseCompanyRecentViews(payload)
      if (type === TYPE.CATEGORY) getCourseCompanyByCategories({ ...payload, limit: LIMIT, categories })
      if (type === TYPE.UNCATEGORIZED) getUncategorizedCourseCompany({ page: nextPage, limit: LIMIT })
    }
  }

  useEffect(() => {
    if (!swiperRef.current) return

    const params = {
      slidesPerView: 4,
      breakpoints: {
        320: {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 8
        },
        720: {
          slidesPerView: 3,
          slidesPerGroup: 3,
          spaceBetween: 8
        },
        1280: {
          slidesPerView: 4,
          slidesPerGroup: 4,
          spaceBetween: 10
        },
        1920: {
          slidesPerView: 5,
          slidesPerGroup: 5,
          spaceBetween: 10
        },
        2560: {
          slidesPerView: 6,
          slidesPerGroup: 6,
          spaceBetween: 16
        },
        3200: {
          slidesPerView: 7,
          slidesPerGroup: 7,
          spaceBetween: 24
        }
      },
      on: {
        init: (swiper) => {
          swiperRef.current = swiper
          setTimeout(() => {
            // swiper.slideTo(initialSlide, 0)
            // 一瞬大きく表示されるのを防止
            setVisible(true)
          }, 0)
        },
        slideChange: (swiper) => {
          setIsBeginning(swiper.isBeginning)
          setIsEnd(swiper.isEnd)
        },
        reachBeginning: () => setIsBeginning(true),
        reachEnd: () => {
          // 初回に発火しないように
          if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false
            return
          }
          setIsEnd(true)
          handleFetchMoreData()
        },
        fromEdge: () => {
          setIsBeginning(false)
          setIsEnd(false)
        }
      },
      virtual: {
        enable: true,
        slides: [],
        renderExternal(vd) {
          setVirtualData(vd)
        }
      }
    }

    Object.assign(swiperRef.current, params)
    if (swiperRef.current.initialize) {
      swiperRef.current.initialize()
    }
  }, [])

  useEffect(() => {
    currentPageRef.current = dataSource.page
  }, [dataSource.page])

  useEffect(() => {
    if (!swiperRef.current) return

    const slides = dataSource.result.map((item) => (
      <SwiperSlide
        key={item.courseId}
        item={item}
        isUserCompany={isUserCompany}
        isNissoken={isNissoken}
        categoryId={categoryId}
      />
    ))
    swiperRef.current.virtual.slides = slides
    setVirtualData((prev) => ({ ...prev, slides }))

    if (swiperRef.current.virtual.update) {
      swiperRef.current.virtual.update()
    }
  }, [dataSource.result])

  useEffect(() => {
    swiperRef.current.params.navigation.prevEl = prevRef.current
    swiperRef.current.params.navigation.nextEl = nextRef.current
    swiperRef.current.navigation.init()
    swiperRef.current.navigation.update()
  }, [prevRef.current, nextRef.current, dataSource])

  const slides = virtualData.slides.map((slide, index) => cloneElement(slide, {
    key: virtualData.from + index,
    type,
    style: { left: virtualData.offset },
    'data-swiper-slide-index': virtualData.from + index
  }))

  return (
    <Wrapper visible={visible} tab={currentTab}>
      <swiper-container
        ref={swiperRef}
        init={false}
        virtual
        slides-per-view={4}
      >
        {slides}
      </swiper-container>

      <button
        ref={prevRef}
        className={`swiper-button-prev ${isBeginning ? 'swiper-button-disabled' : ''}`}
      >
        <LeftOutlined />
      </button>

      <button
        ref={nextRef}
        className={`swiper-button-next ${isEnd ? 'swiper-button-disabled' : ''}`}
      >
        <RightOutlined />
      </button>
    </Wrapper>
  )
}

const SwiperSlide = (props) => {
  const history = useHistories()
  const { profile, userRole } = useProfile()
  const {
    isLiking,
    isUpdatingBookmark,
    courseListTab,
    voteLikeCourse,
    updateBookmarkAction
  } = useCourseList({ userId: profile.userId })

  const { item, isUserCompany, isNissoken, categoryId, type, ...rest } = props

  return (
    <swiper-slide {...rest}>
      <CardCourse
        userId={profile.userId}
        key={item.courseId}
        courseId={item.courseId}
        cardImage={item.imagePath}
        description={item.description}
        descriptionText={item.descriptionText}
        listTag={item.tagCourse}
        name={item.courseName}
        courseOverviewText={item.descriptionText}
        icons={item.tagLesson}
        isShowProgress={false}
        coursePrice={item.price}
        voteLikeCourse={voteLikeCourse}
        startTime={dateFormat(item.startDate, FULL_DATE_TIME)}
        endTime={dateFormat(item.endDate, FULL_DATE_TIME)}
        userRole={userRole}
        course={item}
        isNew={item.statusCourse}
        tab={courseListTab}
        item={item}
        isLiking={isLiking}
        categoryId={categoryId}
        bookmarkType={type}
        isUpdatingBookmark={isUpdatingBookmark}
        updateBookmarkAction={updateBookmarkAction}
        onNavigate={() => {
          if (isUserCompany || isNissoken) {
            history.push({
              pathname: `/course/detail/${item.courseId}`,
              search: `?isCourseList=TRUE&fromTab=${courseListTab}`,
              state: { flagCount: true }
            })
          } else {
            history.push({
              pathname: `/course/detail-unregistered/${item.courseId}?fromTab=${courseListTab}`,
              search: `?isCourseList=TRUE&fromTab=${courseListTab}`,
              state: { flagCount: true }
            })
          }
        }}
      />
    </swiper-slide>
  )
}

export { Swiper, SwiperSlide }
